/**
 * Users page — SuperAdmin CRUDL for UI accounts + their per-instance access
 * grants. Selecting a user reveals their grants (create/update/delete).
 */

import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2, KeyRound } from "lucide-react";
import { PageHeader } from "@/components/common/PageHeader";
import { ConfirmDangerButton } from "@/components/common/ConfirmDangerButton";
import { LoadingRow, EmptyState, ErrorState } from "@/components/common/StateViews";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { userApi, instanceApi, type CreateUserInput } from "@/lib/api";
import { tenantApi, type Tenant } from "@/lib/proxy-api";
import type { UserPublic, InstancePublic, AccessGrantPublic, UiRole } from "@/types";
import { ApiException } from "@/lib/api-client";
import { roleLabel } from "@/lib/tenant-workspace";
import { useHasInstance } from "@/lib/use-instance";
import { useAuth } from "@/store/auth";

const USER_ROLES: UiRole[] = ["tenant_reader", "tenant_writer", "tenant_admin", "super_admin"];
const TENANT_GRANT_ROLES: UiRole[] = ["tenant_reader", "tenant_writer", "tenant_admin"];

type CreateUserForm = CreateUserInput & {
  tenantScope: string;
  tenantAdminKey: string;
};

const EMPTY_CREATE_FORM: CreateUserForm = {
  username: "",
  role: "tenant_reader",
  password: "",
  tenantScope: "",
  tenantAdminKey: "",
};

export function UsersPage() {
  const qc = useQueryClient();
  const [selected, setSelected] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState<CreateUserForm>(EMPTY_CREATE_FORM);
  const [formError, setFormError] = useState<string | null>(null);

  const instance = useAuth((s) => s.instance);
  const hasInstance = useHasInstance();
  const { data, isLoading, error } = useQuery({ queryKey: ["users"], queryFn: () => userApi.list() });
  const { data: tenantData } = useQuery({ queryKey: ["tenants"], queryFn: () => tenantApi.list(), enabled: hasInstance });
  const users: UserPublic[] = data?.users ?? [];
  const tenants: Tenant[] = tenantData?.business ?? [];
  const createsTenantGrant = form.role !== "super_admin";
  const selectedTenant = tenants.find((t) => String(t.id) === form.tenantScope);
  const canSubmitCreate =
    form.username.trim().length > 0 &&
    form.password.length >= 12 &&
    (!createsTenantGrant || (Boolean(instance) && Boolean(form.tenantScope)));

  const createMut = useMutation({
    mutationFn: async () => {
      if (createsTenantGrant && (!instance || !form.tenantScope)) {
        throw new ApiException(400, "BAD_REQUEST", "Select a tenant workspace for this user.");
      }
      const user = await userApi.create({
        username: form.username,
        role: form.role,
        password: form.password,
        email: null,
        mustChangePassword: false,
      });
      if (createsTenantGrant) {
        await userApi.createGrant(user.userId, {
          instanceId: instance!.instanceId,
          tenantScope: form.tenantScope,
          grantRole: form.role,
          tenantName: selectedTenant?.name ?? null,
          tenantAdminKey: form.tenantAdminKey || undefined,
        });
      }
      return user;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["users"] });
      qc.invalidateQueries({ queryKey: ["grants"] });
      setShowCreate(false);
      setForm(EMPTY_CREATE_FORM);
      setFormError(null);
    },
    onError: (err) => setFormError(err instanceof ApiException ? err.message : "Failed to create user"),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => userApi.delete(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["users"] }); setSelected(null); },
  });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Users"
        description="UI login accounts. Grant per-instance access below."
        actions={<Button onClick={() => setShowCreate((v) => !v)}><Plus className="h-4 w-4" /> New user</Button>}
      />

      {showCreate && (
        <form className="rounded-lg border bg-card p-4 space-y-3" onSubmit={(e) => { e.preventDefault(); createMut.mutate(); }}>
          <div className="grid gap-3 lg:grid-cols-5">
            <div className="space-y-1.5">
              <Label htmlFor="u-name">Username</Label>
              <Input id="u-name" value={form.username} onChange={(e) => setForm((f) => ({ ...f, username: e.target.value }))} required autoFocus />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="u-role">UI Role</Label>
              <select id="u-role" className="h-9 w-full rounded-md border border-input bg-transparent px-3 text-sm"
                value={form.role} onChange={(e) => setForm((f) => ({ ...f, role: e.target.value as UiRole, tenantScope: e.target.value === "super_admin" ? "" : f.tenantScope }))}>
                {USER_ROLES.map((role) => <option key={role} value={role}>{roleLabel(role)}</option>)}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="u-pass">Password (min 12)</Label>
              <Input id="u-pass" type="password" value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} required minLength={12} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="u-tenant">Initial tenant</Label>
              <select
                id="u-tenant"
                className="h-9 w-full rounded-md border border-input bg-transparent px-3 text-sm"
                value={form.tenantScope}
                onChange={(e) => setForm((f) => ({ ...f, tenantScope: e.target.value }))}
                disabled={!createsTenantGrant || !hasInstance}
                required={createsTenantGrant}
              >
                <option value="">{hasInstance ? "Select tenant…" : "Select an instance first"}</option>
                {tenants.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="u-key">Tenant Admin key</Label>
              <Input
                id="u-key"
                type="password"
                value={form.tenantAdminKey}
                onChange={(e) => setForm((f) => ({ ...f, tenantAdminKey: e.target.value }))}
                placeholder="Optional if stored"
                disabled={!createsTenantGrant}
                autoComplete="off"
              />
            </div>
          </div>
          {createsTenantGrant && !hasInstance && (
            <p className="text-sm text-muted-foreground">Select an instance before creating a tenant-scoped user.</p>
          )}
          {formError && <p className="text-sm text-destructive">{formError}</p>}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button type="submit" disabled={createMut.isPending || !canSubmitCreate}>Create user</Button>
          </div>
        </form>
      )}

      {isLoading ? (
        <LoadingRow cols={4} />
      ) : error ? (
        <ErrorState message={error instanceof ApiException ? error.message : "Failed to load users"} />
      ) : users.length === 0 ? (
        <EmptyState message="No users yet." />
      ) : (
        <div className="rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Username</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-16 text-right">Delete</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((u) => (
                <TableRow key={u.userId} className={selected === u.userId ? "bg-primary/5" : ""} onClick={() => setSelected(selected === u.userId ? null : u.userId)}>
                  <TableCell className="font-medium">{u.username}</TableCell>
                  <TableCell className="text-muted-foreground">{u.email ?? "—"}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={u.role === "super_admin" ? "bg-primary/10 text-primary border-primary/20" : ""}>
                      {roleLabel(u.role)}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className={u.isActive ? "bg-green-50 text-green-700 border-green-200" : "bg-muted text-muted-foreground"}>
                      {u.isActive ? "active" : "disabled"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <ConfirmDangerButton
                      title="Delete user"
                      resourceName={u.username}
                      actionLabel="Delete user"
                      disabled={deleteMut.isPending}
                      onConfirm={() => deleteMut.mutate(u.userId)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </ConfirmDangerButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {selected && <GrantsPanel userId={selected} users={users} />}
    </div>
  );
}

function GrantsPanel({ userId, users }: { userId: string; users: UserPublic[] }) {
  const user = users.find((u) => u.userId === userId);
  if (!user) return null;
  return (
    <div className="space-y-4 rounded-lg border bg-card p-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Access grants · {user.username}</h2>
          <p className="text-sm text-muted-foreground">Which instances this user may access, and to which tenant.</p>
        </div>
      </div>
      <GrantsList userId={userId} />
    </div>
  );
}

function GrantsList({ userId }: { userId: string }) {
  const qc = useQueryClient();
  const hasInstance = useHasInstance();
  const { data: grantsData, isLoading } = useQuery({
    queryKey: ["grants", userId], queryFn: () => userApi.listGrants(userId),
  });
  const { data: instData } = useQuery({ queryKey: ["instances"], queryFn: () => instanceApi.list() });
  const { data: tenantData } = useQuery({ queryKey: ["tenants"], queryFn: () => tenantApi.list(), enabled: hasInstance });
  const instances: InstancePublic[] = instData?.instances ?? [];
  const tenants: Tenant[] = tenantData?.business ?? [];
  const grants: AccessGrantPublic[] = grantsData?.grants ?? [];

  const [instId, setInstId] = useState("");
  const [tenantScope, setTenantScope] = useState("");
  const [grantRole, setGrantRole] = useState<UiRole>("tenant_reader");
  const [tenantAdminKey, setTenantAdminKey] = useState("");
  const [error, setError] = useState<string | null>(null);

  const createMut = useMutation({
    mutationFn: () =>
      userApi.createGrant(userId, {
        instanceId: instId,
        tenantScope: grantRole === "super_admin" ? "*" : tenantScope,
        grantRole,
        tenantName: tenants.find((t) => String(t.id) === tenantScope)?.name ?? null,
        tenantAdminKey: tenantAdminKey || undefined,
      }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["grants", userId] }); setInstId(""); setTenantScope(""); setTenantAdminKey(""); setError(null); },
    onError: (err) => setError(err instanceof ApiException ? err.message : "Failed to add grant"),
  });
  const deleteMut = useMutation({
    mutationFn: (iid: string) => userApi.deleteGrant(userId, iid),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["grants", userId] }),
  });

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end gap-2">
        <div className="space-y-1.5">
          <Label htmlFor="g-inst">Instance</Label>
          <select id="g-inst" className="h-9 rounded-md border border-input bg-transparent px-3 text-sm min-w-40"
            value={instId} onChange={(e) => setInstId(e.target.value)}>
            <option value="">Select…</option>
            {instances.map((i) => <option key={i.instanceId} value={i.instanceId}>{i.name}</option>)}
          </select>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="g-scope">Tenant</Label>
          <select id="g-scope" className="h-9 rounded-md border border-input bg-transparent px-3 text-sm min-w-52"
            value={tenantScope} onChange={(e) => setTenantScope(e.target.value)} disabled={grantRole === "super_admin"}>
            <option value="">Select tenant…</option>
            {tenants.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="g-role">Grant role</Label>
          <select id="g-role" className="h-9 rounded-md border border-input bg-transparent px-3 text-sm"
            value={grantRole} onChange={(e) => setGrantRole(e.target.value as UiRole)}>
            {TENANT_GRANT_ROLES.map((role) => <option key={role} value={role}>{roleLabel(role)}</option>)}
          </select>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="g-key">Tenant Admin key</Label>
          <Input id="g-key" type="password" value={tenantAdminKey} onChange={(e) => setTenantAdminKey(e.target.value)} placeholder="Optional if already stored" className="w-56" />
        </div>
        <Button disabled={!instId || (!tenantScope && grantRole !== "super_admin") || createMut.isPending} onClick={() => createMut.mutate()}>
          <Plus className="h-4 w-4" /> Add grant
        </Button>
        {error && <p className="text-sm text-destructive">{error}</p>}
      </div>

      {isLoading ? (
        <LoadingRow cols={3} />
      ) : grants.length === 0 ? (
        <p className="text-sm text-muted-foreground py-4">No grants. Add one above.</p>
      ) : (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Instance</TableHead>
                <TableHead>Tenant</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Key</TableHead>
                <TableHead className="w-16 text-right">Delete</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {grants.map((g) => {
                const inst = instances.find((i) => i.instanceId === g.instanceId);
                return (
                  <TableRow key={g.instanceId}>
                    <TableCell className="font-medium">{inst?.name ?? g.instanceId}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{g.tenantScope === "*" ? "all" : (g.tenantName ?? g.tenantScope)}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{roleLabel(g.grantRole)}</Badge>
                    </TableCell>
                    <TableCell>
                      {g.tenantScope === "*" ? (
                        <span className="text-xs text-muted-foreground">instance key</span>
                      ) : (
                        <Badge variant="outline" className={g.hasTenantKey ? "bg-green-50 text-green-700 border-green-200" : "bg-amber-50 text-amber-700 border-amber-200"}>
                          <KeyRound className="h-3 w-3 mr-1" />{g.hasTenantKey ? "set" : "missing"}
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <ConfirmDangerButton
                        title="Delete access grant"
                        resourceName={`${inst?.name ?? g.instanceId}:${g.tenantName ?? g.tenantScope}`}
                        actionLabel="Delete grant"
                        disabled={deleteMut.isPending}
                        onConfirm={() => deleteMut.mutate(g.instanceId)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </ConfirmDangerButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
